import { EmailLinkCallback } from "./pages/authentication/EmailLinkCallback";
import { SmsLinkCallback } from "./pages/authentication/SmsLinkCallback";
import { AuthProfilePrompt } from "./pages/authentication/components/AuthProfilePrompt";
import React, { Suspense } from "react";
import { ChakraProvider, Spinner, Center } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
import LoginPage from "./pages/authentication/LoginPage";
import SecurityPage from "./pages/authentication/SecurityPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import { brandConfig } from "./brandConfig";
import theme from "./theme";

// Module configurations (core tenant modules only)
import clientsModuleConfig from "./pages/clients/moduleConfig";
import governanceModuleConfig from "./pages/governance/moduleConfig";
import { governancePreviewForPath } from "./pages/governance/previews";

/**
 * A module may hand a signed-out visitor something to READ before the sign-in box (2026-09-16;
 * `seamless-page-auth-pattern.md` Part 3). The teaser is written by the module and public-safe — it
 * never imports a page component, so nothing protected reaches the browser. Without this registry the
 * modal opens over a blank page, which is what every tenant did until now.
 */
const ROUTE_PREVIEWS: Record<string, (path: string) => { title: string; blurb: string; node?: React.ReactNode } | undefined> = {
  governance: governancePreviewForPath,
};
import { calendarsModuleConfig } from "./pages/calendars/moduleConfig";
import opportunitiesModuleConfig from "./pages/opportunities/moduleConfig";
import emailsModuleConfig from "./pages/emails/moduleConfig";
import companyKnowledgeModuleConfig from "./pages/companyknowledge/moduleConfig";
import lmModuleConfig from "./pages/lm/moduleConfig";
import mediaCDNModuleConfig from "./pages/media-cdn/moduleConfig";
import pModuleConfig from "./pages/p/moduleConfig";
import rewardsModuleConfig from "./pages/rewards/moduleConfig";
import billsModuleConfig from "./pages/bills/moduleConfig";
import leadflowModuleConfig from "./pages/leadflow/moduleConfig";
import p2ModuleConfig from "./pages/p2/moduleConfig";
import tomModuleConfig from "./pages/tom/moduleConfig";
import prospectingModuleConfig from "./pages/prospecting/moduleConfig";
import employeesModuleConfig from "./pages/employees/moduleConfig";
import shiftsModuleConfig from "./pages/shifts/moduleConfig";
import companiesModuleConfig from "./pages/companies/moduleConfig";
import digitalManagersModuleConfig from "./pages/digital-managers/moduleConfig";
import policiesModuleConfig from "./pages/policies/moduleConfig";
import meetModuleConfig from "./pages/meet/moduleConfig";
import { TomProvider } from "./pages/tom/TomProvider";
const TomPanel = React.lazy(() => import("./pages/tom/TomPanel"));

// Public prospecting imports (NO AUTH REQUIRED — candidate-facing forms)
const PublicPreScreeningForm = React.lazy(
  () => import("./pages/prospecting/public/PublicPreScreeningForm")
);
const PublicProspectChat = React.lazy(
  () => import("./pages/prospecting/public/PublicProspectChat")
);
const PublicReferencePage = React.lazy(
  () => import("./pages/prospecting/public/PublicReferencePage")
);

// Combine all module configurations

/**
 * Hand-written routes (the ones not owned by a module) get their tab name here — the standard's
 * `<Page> | <Site>`, with the site from brandConfig. Module routes need nothing: DocumentTitleManager
 * derives them from each moduleConfig. Never write document.title from a page (2026-09-16).
 */
const STATIC_ROUTE_TITLES: Record<string, string> = {
  "/": "",
  "/login": "Sign in",
  "/auth/sms-link": "Signing in",
  "/auth/email-link": "Signing in",
  "/security": "Security",
  "/onboarding": "Onboarding",
  "/terms": "Terms",
  "/privacy": "Privacy",
};
const modules = [
  governanceModuleConfig, // Groups that decide things together — motions, votes, work, treasury, a permanent record
  clientsModuleConfig,
  calendarsModuleConfig,
  opportunitiesModuleConfig,
  pModuleConfig, // Proposals v2 - Enhanced proposal management with opportunity linking
  billsModuleConfig,
  emailsModuleConfig,
  companyKnowledgeModuleConfig,
  lmModuleConfig, // Learning Management System with course creation and enrollment
  mediaCDNModuleConfig, // Media CDN - File hosting with IPFS storage
  rewardsModuleConfig, // BB Token rewards - issue, track, and redeem tokens
  leadflowModuleConfig, // LeadFlow - Lead capture forms, pipeline management, marketing
  p2ModuleConfig, // P2 Marketplace - Group Buy with pledge campaigns
  tomModuleConfig, // Tom AI - Floating AI assistant panel
  prospectingModuleConfig, // Prospecting - candidate pre-screening pipeline
  employeesModuleConfig, // Employees - workforce roster + onboarding
  shiftsModuleConfig, // Shifts - rostering, check-in/out, timesheets
  companiesModuleConfig, // Companies - employer entity registry (for recruitment scoping)
  digitalManagersModuleConfig, // Digital Managers - long-lived AI "digital employees" with memory + report-to chain
  policiesModuleConfig, // Policies & Procedures — every employee reads; editors publish versions, staff are emailed the delta + acknowledge; per-event audit URLs
  meetModuleConfig, // Meet — self-hosted LiveKit video meetings; public /meet/:room join page
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ModulePageLayout } from "./components/ModulePageLayout";

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color={brandConfig.colors.primary} />
  </Center>
);

// Bill-related imports (special pages not in the module config)
import BillPaymentSuccess from "./pages/bills/BillPaymentSuccess";
const SubscribePage = React.lazy(() => import("./pages/bills/SubscribePage"));

// Public Booking imports (NO AUTH REQUIRED)
import PublicBookingPage from "./pages/calendars/booking/PublicBookingPage";
import PublicBookingEventPage from "./pages/calendars/booking/PublicBookingEventPage";
import PublicBookingConfirmPage from "./pages/calendars/booking/PublicBookingConfirmPage";
import PublicBookingSuccessPage from "./pages/calendars/booking/PublicBookingSuccessPage";
import ManageBooking from "./pages/calendars/ManageBooking";

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { ModulePermissionsProvider, useModulePermissions } from "./contexts/ModulePermissionsContext";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";
import { TenantBrandingLoader } from "./components/TenantBrandingLoader";

// Floating navbar component
import { FloatingNavbar } from "./components/FloatingNavbar";
import { DocumentTitleManager } from "./components/DocumentTitleManager";
import { useAuth } from "./contexts/AuthContext";


const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();

  // Resolve permissionLevel → actual permission strings from backend
  // Modules using the new pattern have permissionLevel on routes/nav and empty permission arrays.
  // Modules not yet migrated still have hardcoded permission arrays — those pass through unchanged.
  const resolvedModules = React.useMemo(() => {
    return modules.map(module => {
      const modulePerms = backendPerms[module.id];
      if (!modulePerms) return module; // Not migrated yet — use hardcoded permissions

      return {
        ...module,
        routes: module.routes?.map((route: any) => {
          const level = (route as any).permissionLevel;
          if (!level) return route; // No permissionLevel — use existing permissions array
          return { ...route, permissions: (modulePerms as any)[level] || [] };
        }),
        navigation: module.navigation?.map((navItem: any) => {
          const level = (navItem as any).permissionLevel;
          if (!level) return navItem; // No permissionLevel — use existing permissions array
          return { ...navItem, permissions: (modulePerms as any)[level] || [] };
        }),
        permissions: {
          ...module.permissions,
          view: modulePerms.view || module.permissions.view,
          create: modulePerms.create || module.permissions.create,
          edit: modulePerms.edit || module.permissions.edit,
          delete: modulePerms.delete || module.permissions.delete,
          ...(modulePerms.financial ? { financial: modulePerms.financial } : {}),
        },
      };
    });
  }, [backendPerms]);

  return (
    <>
      {/* Show floating navbar only when authenticated */}
      {isAuthenticated && <FloatingNavbar modules={resolvedModules} />}

      {/* Tom AI floating panel */}
      {isAuthenticated && (
        <React.Suspense fallback={null}>
          <TomPanel />
        </React.Suspense>
      )}

      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      <DocumentTitleManager modules={modules} staticTitles={STATIC_ROUTE_TITLES}>
      <Routes>
        <Route path="/auth/email-link" element={<EmailLinkCallback />} />
        <Route path="/auth/sms-link" element={<SmsLinkCallback />} />
        {/* Home page */}
        <Route path="/" element={<Home />} />

        {/* Login page */}
        <Route path="/login" element={<LoginPage />} />

        {/* Security & sign-in management — any logged-in user */}
        <Route path="/security" element={<ProtectedRoute requiredPermissions={[]}><SecurityPage /></ProtectedRoute>} />

        {/* Onboarding — staff with the ONBOARDING permission */}
        <Route path="/onboarding" element={<ProtectedRoute requiredPermissions={["ONBOARDING"]}><OnboardingPage /></ProtectedRoute>} />

        {/* PUBLIC BOOKING PAGES (NO AUTH REQUIRED) */}
        {/* These routes allow public visitors to book appointments without logging in */}
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/book/:slug/:eventTypeId" element={<PublicBookingEventPage />} />
        <Route path="/book/:slug/confirm" element={<PublicBookingConfirmPage />} />
        <Route path="/book/:slug/success" element={<PublicBookingSuccessPage />} />
        <Route path="/calendars/booking/manage/:token" element={<ManageBooking />} />

        {/* 🧑‍💼 PUBLIC CANDIDATE PRE-SCREENING FORM (NO AUTH REQUIRED) */}
        <Route
          path="/prospecting/form/:slug"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicPreScreeningForm />
            </Suspense>
          }
        />
        <Route
          path="/prospecting/chat/:slug"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicProspectChat />
            </Suspense>
          }
        />
        {/* Referee link — token is the only credential (prospecting v0.2) */}
        <Route
          path="/prospecting/reference/:token"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicReferencePage />
            </Suspense>
          }
        />

        {/* Special bill routes not in the module */}
        <Route path="/bill/:id/payment-success" element={<BillPaymentSuccess />} />
        {/* Public subscription product landing page */}
        <Route path="/subscribe/:slug" element={<Suspense fallback={<LoadingFallback />}><SubscribePage /></Suspense>} />

        {/* Dynamic Module Routes — permissions resolved from backend for migrated modules */}
        {resolvedModules.map((module) =>
          module.routes?.map((route: any) => {
            const pageComponent = (
              <Suspense fallback={<LoadingFallback />}>
                <route.component />
              </Suspense>
            );

            // If useLayout is true, wrap with ModulePageLayout (auto Navbar + module nav launcher + Container + Footer)
            const content = route.useLayout ? (
              <ModulePageLayout
                moduleConfig={module}
                {...(route.layoutOptions || {})}
              >
                {pageComponent}
              </ModulePageLayout>
            ) : pageComponent;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  (route as any).public ? (
                    content
                  ) : (
                    <ProtectedRoute requiredPermissions={route.permissions || []} preview={ROUTE_PREVIEWS[module.id]?.(route.path)}>
                      {content}
                    </ProtectedRoute>
                  )
                }
              />
            );
          })
        )}

        {/* Profile module disabled for talent/recruitment tenants — staff use /employees/my-details instead */}

      </Routes>
      </DocumentTitleManager>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      // Use the tenantId from brandConfig as default
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router>
      <ApolloProvider client={apolloClient}>
        <IpfsGatewayLoader>
          <AuthProvider>
            <ModulePermissionsProvider>
              <ChakraProvider theme={theme}>
                {/* TenantBrandingLoader must sit INSIDE ChakraProvider: it uses
                    useColorMode() to apply the tenant's default colour mode. */}
                <TenantBrandingLoader>
                  <TomProvider>
                    <AuthProfilePrompt />
                    <AppContent />
                  </TomProvider>
                </TenantBrandingLoader>
              </ChakraProvider>
            </ModulePermissionsProvider>
          </AuthProvider>
        </IpfsGatewayLoader>
      </ApolloProvider>
    </Router>
  );
};
